/**
 * 심리검사 해석 요청 (A35): 점수와 요청문을 "심리검사 해석" 외부 AI 프로젝트로 보낸다.
 * 그 프로젝트에는 상담자가 해설 자료(PDF)를 올려 두고, 그 자료를 1차 근거로 해석하게 한다.
 * 내담자 코드·나이대·성별만 보내고 실명은 보내지 않는다.
 */
import type { AssessmentScore, PsychologicalAssessment } from '../schema/v1';
import { BEGIN, END } from './prompts';
import { scaleLevel, subscaleLevel, TCI_SCALES, TCI_SUBSCALES } from '../assessment/tci';
import { itemLine } from '../assessment/sct';

/** 문장완성검사 해석 칸 (A42) */
export const SCT_SECTIONS = [
  '요약',
  '가족',
  '대인관계',
  '자기개념',
  '정서와 갈등',
  '상담 시사점',
  '해석 상담에서 내담자에게 설명하기',
  '주의점',
] as const;

export const ASSESSMENT_PACKET_VERSION = 'ASSESSMENT_PACKET_V1';

/** 해석 답에 쓰는 칸 제목 (이 순서로) */
export const ASSESSMENT_SECTIONS = [
  '요약',
  '기질',
  '성격',
  '하위척도 특징',
  '상담 시사점',
  '해석 상담에서 내담자에게 설명하기',
  '주의점',
] as const;

/** "심리검사 해석" 프로젝트 지침 (프로젝트 설정에 한 번 붙여넣기) */
export const ASSESSMENT_PROJECT_INSTRUCTION = `당신은 상담자의 심리검사 해석을 돕는 보조자다. 이 프로젝트에 올려 둔 해설 자료(검사 요강·해석 지침)를 1차 근거로 해석한다.

원칙
1. 해석은 이 프로젝트의 해설 자료에 근거한다. 자료에 없는 내용을 단정하지 않고, 일반적인 설명을 덧붙일 때는 그렇다고 밝힌다.
2. 점수는 경향을 말할 뿐 진단이 아니다. 성격을 고정된 것으로 단정하지 않는다. 기질과 성격의 조합, 척도 사이의 관계로 해석한다.
3. 하위척도는 문항 수가 적어 참고자료로만 쓴다(결과지 안내).
4. 상담자가 적어 준 상담 맥락(주호소·치료 방향)이 있으면 연결하되, 맥락에 맞추려고 점수를 무리하게 끼워 맞추지 않는다.
5. 내담자에게 설명하는 문장은 쉬운 말로, 강점과 성장 가능성을 함께 말한다.
6. 출력은 요청한 형식 하나만. 앞뒤 인사말을 쓰지 않고 아티팩트로 만들지 않는다.`;

const fmt = (n: number | undefined) => (n === undefined ? '-' : String(n));

export function scoreTable(scores: AssessmentScore[]): string {
  const main = scores.filter((s) => s.level === 'scale');
  const subs = scores.filter((s) => s.level === 'subscale');
  const out = ['[주 척도] 척도 | 원점수 | T점수 | 백분위 | 수준'];
  for (const d of TCI_SCALES) {
    const s = main.find((x) => x.key === d.key);
    if (s)
      out.push(
        `${d.label}(${d.key}) | ${fmt(s.raw)} | ${fmt(s.t)} | ${fmt(s.percentile)} | ${scaleLevel(s.percentile) ?? '-'}`,
      );
  }
  for (const s of main.filter((x) => !TCI_SCALES.some((d) => d.key === x.key)))
    out.push(
      `${s.label}(${s.key}) | ${fmt(s.raw)} | ${fmt(s.t)} | ${fmt(s.percentile)} | ${scaleLevel(s.percentile) ?? '-'}`,
    );
  if (subs.length) {
    out.push('', '[하위척도 — 참고자료] 척도 | 원점수 | 규준 M (SD) | 규준 대비');
    for (const s of subs) {
      const d = TCI_SUBSCALES[s.key];
      out.push(
        `${s.key} ${d ? `${d.high} ↔ ${d.low}` : s.label} | ${fmt(s.raw)} | ${fmt(s.mean)} (${fmt(s.sd)}) | ${subscaleLevel(s) ?? '-'}`,
      );
    }
  }
  return out.join('\n');
}

export function buildAssessmentPacket(i: {
  clientCode: string;
  ageText?: string | undefined;
  gender?: string | undefined;
  assessment: PsychologicalAssessment;
  context?: { presentingProblems?: string[]; mainTheory?: string | undefined; request?: string };
}): string {
  const a = i.assessment;
  const sct = a.testKind === 'sct' && !!a.items?.length;
  const sections = sct ? SCT_SECTIONS : ASSESSMENT_SECTIONS;
  const guide = sct
    ? [
        '- 영역별(가족·대인관계·자기개념·정서와 갈등)로 반복되는 주제와 응답 양식(회피·짧은 응답·무응답 포함)을 근거 문항 번호와 함께 쓴다.',
        '- 응답은 내담자가 쓴 그대로다. 한 문항만으로 단정하지 않고 여러 문항에서 되풀이되는 것을 본다.',
        '- "손글씨 확인 필요" 문항은 해석의 근거로 약하게만 쓴다.',
      ]
    : ['- 기질은 4척도의 조합(높고 낮음)으로, 성격은 자율성·연대감·자기초월과 기질과의 관계로 해석한다.'];
  return [
    `=== ${ASSESSMENT_PACKET_VERSION} ===`,
    `[검사] ${a.testName}${a.administeredAt ? ` · 검사일 ${a.administeredAt}` : ''}${a.normGroup ? ` · 규준집단 ${a.normGroup}` : ''}${a.missingResponses !== undefined ? ` · 무응답 ${a.missingResponses}` : ''}`,
    `[내담자] ${i.clientCode}${i.ageText ? ` · ${i.ageText}` : ''}${i.gender ? ` · ${i.gender}` : ''}`,
    '',
    sct
      ? `[문항별 응답] (내담자가 쓴 그대로)\n${a.items!.map(itemLine).join('\n')}`
      : a.scores?.length
        ? scoreTable(a.scores)
        : `[점수] ${a.scoresSummary ?? '(없음)'}`,
    '',
    '[상담 맥락]',
    `주호소: ${i.context?.presentingProblems?.length ? i.context.presentingProblems.join(' / ') : '(적지 않음)'}`,
    `주 이론(채택한 치료 방향): ${i.context?.mainTheory ?? '(아직 정하지 않음)'}`,
    `상담자 요청: ${i.context?.request?.trim() || '없음'}`,
    `=== END ${ASSESSMENT_PACKET_VERSION} ===`,
    '',
    '이 프로젝트의 해설 자료를 근거로 위 검사 결과를 해석하라. 아래 제목을 그대로, 이 순서로 쓴다 (### 제목 아래에 문단):',
    ...sections.map((t) => `### ${t}`),
    ...guide,
    '- 상담 시사점에는 상담 관계·개입에서 고려할 점과, 상담 맥락이 있으면 그 연결을 쓴다.',
    '- "해석 상담에서 내담자에게 설명하기"에는 해석 상담(보통 다음 회기)에서 상담자가 그대로 말할 수 있는 쉬운 문장 3~6개를 쓴다.',
    `- 출력 맨 앞에 ${BEGIN}, 맨 끝에 ${END} 를 한 줄씩 쓴다.`,
  ].join('\n');
}

/** 답에서 해석 본문만 (표식 사이, 없으면 전체) */
export function readInterpretation(raw: string): { ok: true; text: string } | { ok: false; error: string } {
  let t = raw.replace(/\r/g, '');
  const b = t.indexOf(BEGIN);
  const e = t.lastIndexOf(END);
  if (b >= 0 && e > b) t = t.slice(b + BEGIN.length, e);
  t = t.replace(/^```(?:markdown|md)?\s*$/gm, '').trim();
  const found = ASSESSMENT_SECTIONS.filter((s) => new RegExp(`^#{2,4}\\s*${s}`, 'm').test(t)).length;
  if (found < 3) return { ok: false, error: '해석 형식이 아닙니다 (### 요약, ### 기질 … 제목이 필요해요)' };
  return { ok: true, text: t };
}

/** 문서에 들어갈 해석 요약: "요약" 칸 (없으면 앞부분) */
export function interpretationSummary(text: string | undefined): string | undefined {
  if (!text) return undefined;
  const m = /^#{2,4}\s*요약\s*\n([\s\S]*?)(?=\n#{2,4}\s|$)/m.exec(text);
  const body = (m ? m[1]! : text).trim();
  return body.length > 600 ? `${body.slice(0, 600)}…` : body;
}

/** 해석 글에서 한 칸의 본문 (### 제목 아래) */
export function interpretationSection(text: string | undefined, title: string): string | undefined {
  if (!text) return undefined;
  const lines = text.replace(/\r/g, '').split('\n');
  const i = lines.findIndex(
    (l) =>
      /^#{1,6}\s/.test(l) &&
      l
        .replace(/^#+\s*/, '')
        .trim()
        .startsWith(title),
  );
  if (i < 0) return undefined;
  const out: string[] = [];
  for (const l of lines.slice(i + 1)) {
    if (/^#{1,6}\s/.test(l)) break;
    if (l.trim()) out.push(l.trim());
  }
  return out.join('\n') || undefined;
}

/**
 * 해석 상담이 아직 남은 검사: 해석을 확인한 날 이후에 승인된 회기가 없으면 다음 회기에 해석 상담을 한다
 * (지도감독 원칙: 1회기에 실시, 2회기에 해석).
 */
export function pendingInterpretationTalks(
  assessments: PsychologicalAssessment[],
  approvedSessionDates: string[],
): { testName: string; lines: string }[] {
  const last = [...approvedSessionDates].sort().at(-1) ?? '';
  return assessments
    .filter((x) => !x.deletedAt && x.interpretationApprovedAt && x.interpretationApprovedAt.slice(0, 10) >= last)
    .map((x) => ({
      testName: x.testName,
      lines:
        interpretationSection(x.interpretation, '해석 상담에서 내담자에게 설명하기') ??
        interpretationSummary(x.interpretation) ??
        '',
    }))
    .filter((x) => x.lines);
}
